import * as React from 'react';

import { cn } from '@/utils';
import { Heading, Icon, Text } from '@/components/ui';

// ContentHeader is a pure composition — Icon + Heading + (optional) Text
// + actions. Lives in business/ because it adds no styling primitives:
// every visual concern (icon colour/size, heading scale, body tone) is
// delegated to the primitives it composes; the JSX itself only carries
// layout / spacing utilities.
//
// `tone` is forwarded to <Icon tone> so the icon paints in the chosen
// brand colour. No shell, no chrome wrapper.
//
// `capitalizeFirst` keeps the existing i18next contract: glossary keys
// (`glossary.holidays` → "holidays") and similar lowercase noun keys
// are reusable mid-sentence, but the page header needs them to render
// as proper titles. We promote only the first character — anything
// already capitalized (modal titles like "Edit API Key", proper nouns
// inside the string) is preserved untouched.
const capitalizeFirst = value => {
    if (typeof value !== 'string' || value.length === 0) {
        return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
};
const ContentHeader = React.forwardRef(({ children = null, className: _className, style: _style, description, icon, title, tone = 'primary', ...props }, ref) => {
    const hasDescription = Boolean(description);
    const iconSize = hasDescription ? 'x-large' : 'small';

    return (
        <div
            ref={ref}
            className={cn('tw-flex tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between')}
            {...props}
        >
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                {icon ? (
                    <Icon icon={icon} size={iconSize} tone={tone} />
                ) : null}
                <div className="tw-min-w-0 tw-space-y-1">
                    {/* Page titles often arrive lowercased from the
                        glossary (`i18next.t('glossary.holidays')` →
                        "holidays"). We capitalize the first letter
                        here so the glossary convention (lowercase
                        noun for mid-sentence use) stays intact while
                        the page header still reads as a proper title. */}
                    <Heading as="h2" variant="title-lg">
                        {capitalizeFirst(title)}
                    </Heading>
                    {description ? (
                        <Text tone="muted" variant="body">
                            {description}
                        </Text>
                    ) : null}
                </div>
            </div>

            {children ? (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 lg:tw-justify-end">
                    {children}
                </div>
            ) : null}
        </div>
    );
});

ContentHeader.displayName = 'ContentHeader';

export { ContentHeader };
